// @ts-strict-ignore
import React, {
  useContext, useMemo, useEffect,
} from 'react';
import { Button, ButtonSize, ButtonVariant } from '@usealloy/component-library';
import isEqual from 'lodash/isEqual';

import { selectCustomLists, selectWorkflowData, selectWorkflowProps } from 'flow/store/redux/selectors';
import type {
  Threshold,
  DataRoute,
  Attribute,
  AttributeComponent,
} from 'flow/store/engine/types';

import { populateDataRouteData } from 'flow/service/dataRoutes/dataRoutes';
import { populateThresholdData } from 'flow/service/thresholds';
import { useAppSelector } from '~/store/reduxHooks';

import { IsolatedEditorContainer, StyledPre } from '../../../styles';
import MLAComponentDropdown from '../MLAComponentDropdown';
import { MLAEditorContext } from '../context';
import { AttributeEditorContent } from '../styles';
import { RuleEditorContext } from '../../NodeEditorDrawer/context';
import { generateCustomAttributeTokens, getMLADescriptionTokens, getSortedMLAComponentsByPrecedence } from '../../../../service/mla';
import AddAttributeFiltersDropdown from './AddAttributeFiltersDropdown';
import type { AttributeDescriptionTokens } from '../types';
import { MLAAttributeItemOrigin } from '../../NodeEditorDrawer/types';
import {
  updateCustomListAttribute, isCustomListAttribute,
} from 'flow/service/actions/services';

interface MLAAttributeItemProps {
  origin: MLAAttributeItemOrigin;
  data: {
    threshold?: Threshold;
    dataRoute?: DataRoute;
  };
  indentLevel?: number;
}

function MLAAttributeContainer({
  origin,
  data,
  indentLevel,
}: MLAAttributeItemProps) {
  const { actions } = useAppSelector(selectWorkflowData);
  const { reasonCodeGroups, services, universalAttributes } = useAppSelector(selectWorkflowProps);
  const customLists = useAppSelector(selectCustomLists);

  const {
    setIsolatedEditingBlock,
  } = useContext(RuleEditorContext);

  const {
    sourceData,
    setSourceData,
    setMorphingCaretPosition,
    mlaEditorPanelBoxContainerRef,
    handleCloseMLAEditor,
  } = useContext(MLAEditorContext);

  const extendedData = useMemo(() => {
    if (origin.startsWith('threshold')) {
      return populateThresholdData({
        actions,
        reasonCodeGroups,
        services,
        threshold: data.threshold,
      });
    }

    return populateDataRouteData({
      actions,
      services,
      dataRoute: data.dataRoute,
      universalAttributes,
    });
  }, [actions, data, origin, reasonCodeGroups, services, universalAttributes]);

  const thresholdSide = useMemo(() => {
    if (
      origin === MLAAttributeItemOrigin.THRESHOLD_LEFT
      || origin === MLAAttributeItemOrigin.THRESHOLD_RIGHT
    ) {
      return origin === MLAAttributeItemOrigin.THRESHOLD_LEFT
        ? 'left'
        : 'right';
    }

    return null;
  }, [origin]);

  const attributeData = useMemo(() => {
    let descString: string;
    let attribute: Attribute;
    let components: AttributeComponent[];
    let attributeDataPath: string;
    if (thresholdSide) {
      descString = extendedData[thresholdSide].attribute.description;
      attribute = extendedData[thresholdSide].attribute;
      components = extendedData[thresholdSide].attributeComponents;
      attributeDataPath = `${extendedData.publicId}.${thresholdSide}.attributeComponents`;
    }

    if (origin === MLAAttributeItemOrigin.DATAROUTE && 'attribute' in extendedData) {
      descString = extendedData.attribute.details.description;
      attribute = extendedData.attribute.details;
      components = extendedData.attribute.multiLevelAttributeFilter.filterComponents;
      attributeDataPath = `${extendedData.publicId}.attribute.multiLevelAttributeFilter.filterComponents`;
    }

    if (descString.endsWith('(inclusive)')) {
      descString = descString.replace('(inclusive)', '');
    } else if (descString.endsWith('(Inclusive)')) {
      descString = descString.replace('(Inclusive)', '');
    }

    if (isCustomListAttribute(attribute) && 'left' in extendedData) {
      const leftActionOptions = extendedData.left.action.options;
      const updatedDetails = updateCustomListAttribute({
        components,
        customLists,
        actionOptions: leftActionOptions,
        description: descString,
      });
      components = updatedDetails.components;
      descString = updatedDetails.description;
    }

    const multiLevelAttributesWithCustomSorting = ['th_multilevel', 'th_nonmonetary_multilevel'].includes(attribute.attribute_name);
    // for multiLevelAttributesWithCustomSorting attributes, we generate tokens using filter components, not description string
    const descTokens = multiLevelAttributesWithCustomSorting
      ? getSortedMLAComponentsByPrecedence({ mlaData: components })
      : getMLADescriptionTokens(descString);

    let tokensWithIndex: AttributeDescriptionTokens = [];

    tokensWithIndex = generateCustomAttributeTokens({
      attribute,
      components,
      descTokens,
    });

    return {
      descTokens: tokensWithIndex,
      components,
      attributeDataPath,
      attribute,
    };
  }, [extendedData, origin, thresholdSide, customLists]);

  useEffect(() => {
    const sourceDataObject = data?.threshold || data?.dataRoute;
    if (attributeData && !isEqual(sourceData.data, sourceDataObject)) {
      setSourceData({
        type: origin,
        data: sourceDataObject,
        attributeTokenMap: attributeData,
      });
    }
  }, [attributeData, sourceData, data, origin, setSourceData]);

  const clearMlaEditorState = () => {
    setSourceData({
      type: undefined,
      data: undefined,
      attributeTokenMap: undefined,
    });

    setMorphingCaretPosition('');

    handleCloseMLAEditor();
    setIsolatedEditingBlock({
      originPublicId: undefined,
      originType: undefined,
      originCaretIndex: undefined,
    });
  };

  return (
    <IsolatedEditorContainer
      ref={mlaEditorPanelBoxContainerRef}
      indentLevel={indentLevel}
    >
      <AttributeEditorContent>
        {sourceData?.attributeTokenMap?.descTokens?.map((token, i) => (
          token.caretIndex !== undefined ? (
            <MLAComponentDropdown
              key={token.string}
              token={token}
              componentPath={`${attributeData.attributeDataPath}`}
              mlaData={attributeData?.components}
            />
          ) : (
            <StyledPre key={`${token.string}-${i + 1}`}>{token.string}</StyledPre>
          )
        ))}
      </AttributeEditorContent>
      <AttributeEditorContent
        style={{ justifyContent: 'space-between' }}
      >
        <AddAttributeFiltersDropdown
          componentPath={`${attributeData.attributeDataPath}`}
          mlaData={attributeData?.components}
        />
        <div style={{ display: 'flex', columnGap: '6px' }}>
          <Button
            data-testid="editMLAAttributeDoneButton"
            variant={ButtonVariant.primary}
            size={ButtonSize.small}
            onClick={clearMlaEditorState}
          >
            Done
          </Button>
        </div>
      </AttributeEditorContent>
    </IsolatedEditorContainer>
  );
}

export default MLAAttributeContainer;
